import { ResponsiveBar } from '@nivo/bar';
import { ChartFrame, type ChartCardProps } from '~/components/dashboard/charts/ChartCard';
import {
  CHART_AXIS,
  CHART_MARGIN,
  CHART_SERIES_TOKENS,
} from '~/components/dashboard/charts/chartConfig';
import { ChartLegend } from '~/components/dashboard/charts/ChartLegend';
import { useChartTheme } from '~/components/dashboard/charts/useChartTheme';
import { thinTicks } from '~/lib/chartTicks';
import type { TokenName } from '~/lib/theme-colors';

export interface BarChartKey {
  /** The key in each row that holds this series' value. */
  id: string;
  label: string;
  token?: TokenName;
}

export interface BarChartCardProps extends ChartCardProps {
  /** One row per x value: `{ label: '05-12', input: 10, output: 4 }`. */
  rows: ({ label: string } & Record<string, number | string>)[];
  keys: BarChartKey[];
  /** `grouped` puts the series side by side instead of stacking them. */
  grouped?: boolean;
  /** d3 format for the tooltip and y axis. */
  valueFormat?: string;
}

/** Bars per x value, stacked by default; a legend only for more than one series (DESIGN.md §8.1). */
export function BarChartCard({
  rows,
  keys,
  grouped,
  valueFormat = ' >-,.0f',
  ...card
}: BarChartCardProps) {
  const { nivoTheme, color } = useChartTheme();
  const colors = keys.map((key, index) =>
    color(key.token ?? CHART_SERIES_TOKENS[index % CHART_SERIES_TOKENS.length]),
  );
  const xTicks = thinTicks(rows.map((row) => row.label));

  return (
    <ChartFrame
      {...card}
      isEmpty={!rows.some((row) => keys.some((key) => Number(row[key.id]) > 0))}
      legend={
        keys.length > 1 && (
          <ChartLegend
            items={keys.map((key, index) => ({
              id: key.id,
              label: key.label,
              color: colors[index],
            }))}
          />
        )
      }
    >
      <ResponsiveBar
        data={rows}
        keys={keys.map((key) => key.id)}
        indexBy="label"
        theme={nivoTheme}
        colors={colors}
        margin={CHART_MARGIN}
        padding={0.25}
        groupMode={grouped ? 'grouped' : 'stacked'}
        valueScale={{ type: 'linear', min: 0, max: 'auto' }}
        valueFormat={valueFormat}
        enableLabel={false}
        enableGridY
        axisTop={null}
        axisRight={null}
        axisBottom={{ ...CHART_AXIS, tickValues: xTicks }}
        axisLeft={{ ...CHART_AXIS, format: valueFormat }}
        // shortcut: bar animation throws under the pinned @react-spring 9.4.5; re-enable once it matches @nivo 0.99.
        animate={false}
      />
    </ChartFrame>
  );
}
